import { Controller } from "@hotwired/stimulus"

/**
 * Manages flash message "toasts" with auto-dismiss and progress bar.
 * Handles pausing on hover/focus and visibility changes (tab switching).
 */
export default class extends Controller {
  static targets = ["progress"]
  static values = {
    // Time in milliseconds before the toast disappears.
    timeout: { type: Number, default: 6000 },
    // Duration of the exit animation in milliseconds.
    exitDuration: { type: Number, default: 220 },
    // Whether to show the progress bar.
    progress: { type: Boolean, default: true },
  }

  connect() {
    this.closing = false
    this.autoDismissTimer = null
    this.progressFrame = null
    this.transitionFallbackTimer = null
    this.remainingTime = this.timeoutValue
    this.startedAt = null

    this.boundTransitionEnd = this.handleTransitionEnd.bind(this)
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)

    document.addEventListener("visibilitychange", this.boundVisibilityChange)

    requestAnimationFrame(() => {
      if (!this.element.isConnected || this.closing) return

      this.element.classList.add("is-visible")
      this.resume()
    })
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    this.element.removeEventListener("transitionend", this.boundTransitionEnd)
    this.clearTimers()
  }

  /**
   * Closes the toast with an animation.
   */
  close(event) {
    event?.preventDefault()
    if (this.closing) return

    this.closing = true
    this.clearTimers()
    this.element.removeEventListener("transitionend", this.boundTransitionEnd)
    this.element.addEventListener("transitionend", this.boundTransitionEnd)
    this.element.classList.remove("is-visible")
    this.element.classList.add("is-leaving")

    if (!this.hasTransition()) {
      this.remove()
      return
    }

    this.transitionFallbackTimer = window.setTimeout(() => this.remove(), this.exitDurationValue + 80)
  }

  /**
   * Pauses the auto-dismiss timer (e.g. on mouse enter).
   */
  pause() {
    if (!this.canAutoDismiss()) return
    if (this.autoDismissTimer === null || this.startedAt === null) return

    const elapsed = performance.now() - this.startedAt
    this.remainingTime = Math.max(this.remainingTime - elapsed, 0)
    this.clearAutoDismissTimer()
    this.cancelProgressFrame()
    this.updateProgress()
  }

  /**
   * Resumes the auto-dismiss timer (e.g. on mouse leave).
   */
  resume() {
    if (!this.canAutoDismiss()) return
    if (document.visibilityState === "hidden") return

    if (this.remainingTime <= 0) {
      this.close()
      return
    }

    this.startedAt = performance.now()
    this.clearAutoDismissTimer()
    this.autoDismissTimer = window.setTimeout(() => this.close(), this.remainingTime)
    this.startProgressLoop()
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.pause()
    } else {
      this.resume()
    }
  }

  handleTransitionEnd(event) {
    if (event.target !== this.element) return
    this.remove()
  }

  /**
   * Starts the animation frame loop for the progress bar.
   */
  startProgressLoop() {
    if (!this.progressValue || !this.hasProgressTarget || this.timeoutValue <= 0) return

    this.cancelProgressFrame()
    this.updateProgress()

    const tick = () => {
      if (!this.canAutoDismiss() || this.autoDismissTimer === null) return

      this.updateProgress()
      this.progressFrame = requestAnimationFrame(tick)
    }

    this.progressFrame = requestAnimationFrame(tick)
  }

  /**
   * Updates the --toast-progress CSS variable.
   */
  updateProgress() {
    if (!this.progressValue || !this.hasProgressTarget || this.timeoutValue <= 0) return

    const elapsed = this.startedAt ? performance.now() - this.startedAt : 0
    const remaining = this.autoDismissTimer === null ? this.remainingTime : Math.max(this.remainingTime - elapsed, 0)
    const ratio = Math.max(Math.min(remaining / this.timeoutValue, 1), 0)

    this.progressTarget.style.setProperty("--toast-progress", ratio.toString())
  }

  clearTimers() {
    this.clearAutoDismissTimer()
    this.cancelProgressFrame()

    if (this.transitionFallbackTimer) {
      window.clearTimeout(this.transitionFallbackTimer)
      this.transitionFallbackTimer = null
    }
  }

  clearAutoDismissTimer() {
    if (this.autoDismissTimer) {
      window.clearTimeout(this.autoDismissTimer)
      this.autoDismissTimer = null
    }

    this.startedAt = null
  }

  cancelProgressFrame() {
    if (this.progressFrame) {
      cancelAnimationFrame(this.progressFrame)
      this.progressFrame = null
    }
  }

  canAutoDismiss() {
    return !this.closing && this.timeoutValue > 0
  }

  /**
   * Detects if the element has CSS transitions to wait for before removal.
   */
  hasTransition() {
    const styles = window.getComputedStyle(this.element)
    const durations = styles.transitionDuration.split(",").map((value) => this.durationToMs(value))
    const delays = styles.transitionDelay.split(",").map((value) => this.durationToMs(value))

    return durations.some((duration, index) => duration + (delays[index] || 0) > 0)
  }

  durationToMs(value) {
    const trimmed = value.trim()
    if (trimmed.endsWith("ms")) return parseFloat(trimmed)
    if (trimmed.endsWith("s")) return parseFloat(trimmed) * 1000

    return 0
  }

  remove() {
    this.clearTimers()
    this.element.removeEventListener("transitionend", this.boundTransitionEnd)
    this.element.remove()
  }
}
